// admin/src/pages/AdminHome.js
import React, { useState, useEffect } from "react";
import { db, CLOUDINARY_CLOUD_NAME, CLOUDINARY_UPLOAD_PRESET } from "../firebase";
import { doc, getDoc, setDoc } from "firebase/firestore";
import "../AdminHome.css";
import AdminNotificationsBell from "./AdminNotificationsBell";

export default function AdminHome() {
  const [loading, setLoading] = useState(true);
  const [content, setContent] = useState({
    title: "",
    subtitle: "",
    bannerText: "",
    sliderImages: [],
    trending: []
  });

  const refDoc = doc(db, "homepage", "homeContent");

  // ------------------------------
  // LOAD HOMEPAGE DATA
  // ------------------------------
  useEffect(() => {
    const load = async () => {
      try {
        const snap = await getDoc(refDoc);
        if (snap.exists()) {
          setContent(snap.data());
        } else {
          const defaultData = {
            title: "Welcome to SS Fashion",
            subtitle: "Where tradition meets modern elegance.",
            bannerText: "",
            sliderImages: [],
            trending: []
          };

          await setDoc(refDoc, defaultData, { merge: true });
          setContent(defaultData);
        }
      } catch (e) {
        console.error(e);
        alert("Failed loading homepage data");
      }
      setLoading(false);
    };

    load();
  }, []);

  // ----------------------------------
  // CLOUDINARY UPLOAD FUNCTION
  // ----------------------------------
  const uploadToCloudinary = async (file) => {
    if (!CLOUDINARY_CLOUD_NAME || !CLOUDINARY_UPLOAD_PRESET) {
      alert("Cloudinary ENV variables missing!");
      return;
    }

    const url = `https://api.cloudinary.com/v1_1/${CLOUDINARY_CLOUD_NAME}/image/upload`;

    const fd = new FormData();
    fd.append("file", file);
    fd.append("upload_preset", CLOUDINARY_UPLOAD_PRESET);

    const res = await fetch(url, {
      method: "POST",
      body: fd
    });

    if (!res.ok) throw new Error("Cloudinary upload failed");

    const data = await res.json();
    return data.secure_url;
  };

  // ---------------------------------------------------------------
  // HANDLE IMAGE UPLOAD (SLIDER / TRENDING)
  // ---------------------------------------------------------------
  const handleUpload = async (e, type, index) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      const secureUrl = await uploadToCloudinary(file);

      if (type === "slider") {
        const arr = [...content.sliderImages];
        arr[index] = secureUrl;
        setContent({ ...content, sliderImages: arr });
      } else {
        const arr = [...content.trending];
        arr[index] = { ...arr[index], image: secureUrl };
        setContent({ ...content, trending: arr });
      }

      alert("Image uploaded successfully!");
    } catch (err) {
      console.error(err);
      alert("Upload failed: " + err.message);
    }
  };

  const addSliderImage = () => {
    setContent({
      ...content,
      sliderImages: [...content.sliderImages, ""]
    });
  };

  const addTrendingItem = () => {
    setContent({
      ...content,
      trending: [...content.trending, { name: "", image: "" }]
    });
  };

  const saveChanges = async () => {
    try {
      await setDoc(refDoc, content, { merge: true });
      alert("Home Page Updated!");
    } catch (err) {
      alert("Saving failed: " + err.message);
    }
  };

  if (loading) return <h3 style={{ padding: 20 }}>Loading...</h3>;

  return (
    <div className="admin-home" style={{ padding: 20, maxWidth: 900 }}>

      {/* 🔔 HEADER WITH NOTIFICATION BELL */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 20
        }}
      >
        <h2>Admin — Update Home Page</h2>
      </div>

      <label>Title</label>
      <input
        value={content.title}
        onChange={(e) => setContent({ ...content, title: e.target.value })}
        className="input-box"
      />

      <label>Subtitle</label>
      <input
        value={content.subtitle}
        onChange={(e) => setContent({ ...content, subtitle: e.target.value })}
        className="input-box"
      />
      <label>Mobile Banner Text (Optional)</label>
      <input
        value={content.bannerText || ""}
        onChange={(e) =>
          setContent({ ...content, bannerText: e.target.value })
        }
        className="input-box"
        placeholder="Example: Mega Festive Offers | Free Shipping Today"
      />
      <p style={{ fontSize: 12, color: "#666", marginTop: -6 }}>
        Leave empty to automatically show "Today Trending"
      </p>

      {/* SLIDER IMAGES */}
      <h3 style={{ marginTop: 20 }}>Slider Images</h3>

      {content.sliderImages.map((img, index) => (
        <div key={index} style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 12 }}>
          <img
            src={img || "/placeholder.jpg"}
            alt=""
            style={{ width: 150, height: 90, borderRadius: 8, objectFit: "cover", border: "1px solid #ddd" }}
          />

          <input type="file" accept="image/*" onChange={(e) => handleUpload(e, "slider", index)} />

          <button
            onClick={() => {
              const arr = [...content.sliderImages];
              arr.splice(index, 1);
              setContent({ ...content, sliderImages: arr });
            }}
          >
            Remove
          </button>
        </div>
      ))}

      <button className="add-btn" onClick={addSliderImage}>
        + Add Slider Image
      </button>

      {/* TRENDING ITEMS */}
      <h3 style={{ marginTop: 30 }}>Trending Collections</h3>

      {content.trending.map((t, index) => (
        <div key={index} style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 12 }}>
          <input
            placeholder="Name"
            value={t.name}
            onChange={(e) => {
              const arr = [...content.trending];
              arr[index] = { ...arr[index], name: e.target.value };
              setContent({ ...content, trending: arr });
            }}
            className="input-box"
            style={{ width: 150 }}
          />

          <img
            src={t.image || "/placeholder.jpg"}
            alt=""
            style={{ width: 80, height: 80, borderRadius: 8, objectFit: "cover", border: "1px solid #ddd" }}
          />

          <input type="file" accept="image/*" onChange={(e) => handleUpload(e, "trending", index)} />

          <button
            onClick={() => {
              const arr = [...content.trending];
              arr.splice(index, 1);
              setContent({ ...content, trending: arr });
            }}
          >
            Remove
          </button>
        </div>
      ))}

      <button className="add-btn" onClick={addTrendingItem}>
        + Add Trending Item
      </button>

      <div style={{ marginTop: 30 }}>
        <button className="save-btn" onClick={saveChanges}>
          Save Changes
        </button>
      </div>
    </div>
  );
}
